import { motion } from 'framer-motion'
import { Eye, Users, MessageCircleOff, ShieldX } from 'lucide-react'

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
}

const impacts = [
    {
        icon: Eye,
        color: 'from-rose-500 to-pink-600',
        bg: 'bg-rose-50',
        border: 'border-rose-100',
        title: 'Outing como instrumento',
        text: 'O outing como instrumento de intimidação e/ou retaliação (revelar ou ameaçar revelar questões relacionadas com orientação sexual, identidade de género e características sexuais);'
    },
    {
        icon: MessageCircleOff,
        color: 'from-rose-500 to-pink-600',
        bg: 'bg-rose-50',
        border: 'border-rose-100',
        title: 'Estigmatização e autoculpabilização',
        text: 'A violência associada à estigmatização das suas identidades – que pode induzir a autoculpabilização das vítimas pelas agressões sofridas;'
    },
    {
        icon: Users,
        color: 'from-rose-500 to-pink-600',
        bg: 'bg-rose-50',
        border: 'border-rose-100',
        title: 'Isolamento social',
        text: 'O isolamento das pessoas LGBTI+, que, devido à, por vezes, reduzida dimensão das suas redes de apoio, pode dificultar o pedido de ajuda por parte da vítima;'
    },
    {
        icon: ShieldX,
        color: 'from-rose-500 to-pink-600',
        bg: 'bg-rose-50',
        border: 'border-rose-100',
        title: 'Receio de denunciar',
        text: 'O receio de dar conhecimento da vitimação sofrida às autoridades, muitas vezes associado à dificuldade em revelar factos e aspetos da sua vida relacionados com a sua sexualidade e ao receio de reforçar o estigma da ligação às pessoas da comunidade LGBTI+;'
    }
]

export default function ConsequenciaImpacto() {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen bg-white"
        >
            <title>Consequências da Violência contra Pessoas LGBTI+ | APAV</title>
            <meta name="description" content="A violência contra pessoas LGBTI+ tem consequências próprias: outing, estigmatização, autoculpabilização, isolamento social e receio de denunciar." />
            <link rel="canonical" href="https://lgbti.apav.pt/ConsequenciaImpacto" />
            {/* Hero Section */}
            <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-brand-900 py-20 lg:py-28">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-80 h-80 bg-purple-400 rounded-full filter blur-3xl translate-x-1/2 -translate-y-1/2" />
                    <div className="absolute bottom-0 left-0 w-80 h-80 bg-brand-400 rounded-full filter blur-3xl -translate-x-1/2 translate-y-1/2" />
                </div>
                <div className="container-custom relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                    >
                        <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                            Consequências e Impacto
                        </h1>
                    </motion.div>
                </div>
            </section>

            {/* Intro Quote */}
            <section className="py-10 lg:py-12 bg-white">
                <div className="container-custom">
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <blockquote className="text-xl lg:text-2xl font-bold text-slate-800 leading-relaxed border-l-4 border-brand-500 pl-8 py-2">
                            A violência contra pessoas LGBTI+ tem particularidades que se manifestam tanto na natureza do próprio ato violento, como nas consequências da situação de crime para a vítima e/ou pessoas próximas.
                        </blockquote>
                    </motion.div>
                </div>
            </section>

            {/* Impact Cards */}
            <section className="py-6 pb-2 lg:pb-4 bg-slate-50">
                <div className="container-custom">
                    <div className="grid md:grid-cols-2 gap-6">
                        {impacts.map((item, i) => {
                            const Icon = item.icon
                            return (
                                <motion.div
                                    key={i}
                                    variants={fadeUp}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: i * 0.1 }}
                                    className={`${item.bg} ${item.border} border rounded-2xl p-7 hover:-translate-y-1 transition-transform duration-300 shadow-xs`}
                                >
                                    <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${item.color} shadow-md mb-5`}>
                                        <Icon size={22} className="text-white" />
                                    </div>
                                    <h2 className="text-lg font-bold text-slate-900 mb-3">{item.title}</h2>
                                    <p className="text-slate-700 leading-relaxed">{item.text}</p>
                                </motion.div>
                            )
                        })}
                    </div>
                </div>
            </section>

            {/* Closing Section */}
            <section className="pt-2 pb-10 lg:pt-4 lg:pb-12 bg-white">
                <div className="container-custom">
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="bg-gradient-to-br from-brand-600 to-purple-700 rounded-2xl p-10 text-white relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                            <p className="text-lg leading-relaxed relative z-10 font-medium">
                                Estes constrangimentos podem ainda decorrer de experiências anteriores de discriminação e/ou de pedidos de ajuda previamente malsucedidos, que agudizam o isolamento e, consequentemente, a vulnerabilidade.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>
        </motion.div>
    )
}
